import React from 'react';
import { Building2, Scale, CheckCircle, Clock, Banknote, Phone, Mail } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const ICON_MAP = { Building2, Scale };

const ServiceSelector = ({ services, onServiceSelect }) => {
  return (
    <div className="space-y-8">
      {/* Section header */}
      <div className="space-y-1.5">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          Choisissez votre type de consultation
        </h2>
        <p className="text-sm text-muted-foreground">
          Sélectionnez le service qui correspond à vos besoins pour commencer.
        </p>
      </div>

      {/* Service cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {services?.map((service) => {
          const IconComp = ICON_MAP?.[service?.icon] || Building2;
          return (
            <div
              key={service?.id}
              onClick={() => onServiceSelect?.(service)}
              className={cn(
                'group relative bg-card border border-border rounded-lg p-6 cursor-pointer',
                'transition-all duration-200 hover:border-primary/40 hover:shadow-sm',
                'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2'
              )}
            >
              {/* Icon */}
              <div className="flex items-center justify-center w-11 h-11 rounded-md bg-muted mb-5 group-hover:bg-primary/10 transition-colors">
                <IconComp className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>

              {/* Info */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-1">
                    {service?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {service?.description}
                  </p>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {service?.duration} min
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Banknote className="w-3.5 h-3.5" />
                    {service?.price}
                  </span>
                </div>

                {/* Features */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-foreground uppercase tracking-wide">
                    Inclus
                  </p>
                  <ul className="space-y-1">
                    {service?.features?.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <Button
                  variant="default"
                  size="sm"
                  fullWidth
                  onClick={(e) => {
                    e?.stopPropagation();
                    onServiceSelect?.(service);
                  }}
                >
                  Choisir ce service
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Help banner */}
      <div className="bg-muted/40 border border-border rounded-lg p-5">
        <p className="text-sm font-medium text-foreground mb-1">Besoin d'aide pour choisir ?</p>
        <p className="text-xs text-muted-foreground mb-4">
          Nos conseillers peuvent vous aider à identifier le service le plus adapté à votre situation.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Phone"
            iconPosition="left"
            asChild
          >
            <a href="tel:+2252150005318">Appeler maintenant</a>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Mail"
            iconPosition="left"
            asChild
          >
            <a href="mailto:contact@kapitalstates.com">Envoyer un email</a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceSelector;